
import { useEffect, useState } from "react";
import {
collection,
onSnapshot,
deleteDoc,
doc,
query,
orderBy
} from "firebase/firestore";

import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";

import {
Search,
Trash2,
Trophy,
BarChart3,
ChevronDown,
ChevronUp
} from "lucide-react";

import jsPDF from "jspdf";

export default function AdminTimeAnalysis(){

const { orgId } = useAuth();

const [logs,setLogs] = useState([]);
const [search,setSearch] = useState("");

const [showAllEmployees,setShowAllEmployees]=useState(false);
const [showAllClients,setShowAllClients]=useState(false);


/* ---------------- LOAD TIME LOGS ---------------- */

useEffect(()=>{

if(!orgId) return;

const q=query(
collection(db,"organizations",orgId,"timeLogs"),
orderBy("createdAt","desc")
);

const unsub=onSnapshot(q,(snap)=>{

setLogs(
snap.docs.map(d=>({
id:d.id,
...d.data()
}))
);

});

return ()=>unsub();

},[orgId]);



/* ---------------- DELETE LOG ---------------- */

const deleteLog=async(id)=>{

if(!window.confirm("Delete this log?")) return;

await deleteDoc(
doc(db,"organizations",orgId,"timeLogs",id)
);

};



/* ---------------- FORMAT TIME ---------------- */

const formatTime=(seconds)=>{

if(!seconds) return "0h 0m";

const h=Math.floor(seconds/3600);
const m=Math.floor((seconds%3600)/60);

return `${h}h ${m}m`;

};



/* ---------------- SEARCH ---------------- */

const filtered = logs.filter(l=>{

const term=search.toLowerCase();

return(
(l.employeeName||"").toLowerCase().includes(term) ||
(l.projectName||"").toLowerCase().includes(term)
);

});



/* ---------------- EMPLOYEE TOTAL ---------------- */

const employeeTotals={};

logs.forEach(log=>{

const emp=log.employeeName || "Unknown";

if(!employeeTotals[emp]) employeeTotals[emp]=0;

employeeTotals[emp]+=log.duration || 0;

});

const employeeRanking =
Object.entries(employeeTotals)
.sort((a,b)=>b[1]-a[1]);



/* ---------------- CLIENT TOTAL ---------------- */

const clientTotals={};

logs.forEach(log=>{

const client=log.clientName || "Unknown";

if(!clientTotals[client]) clientTotals[client]=0;

clientTotals[client]+=log.duration || 0;

});



/* ---------------- INSIGHTS ---------------- */

const insights=[];

if(employeeRanking.length>0){

const [topEmployee,topTime]=employeeRanking[0];
const hours=topTime/3600;

insights.push(`🏆 Top performer: ${topEmployee} (${hours.toFixed(1)} hrs)`);

}

employeeRanking.forEach(([name,time])=>{

const hours=time/3600;

if(hours>45){
insights.push(`⚠ ${name} may be overworked (${hours.toFixed(1)} hrs)`);
}

if(hours>0 && hours<3){
insights.push(`📉 ${name} has low activity (${hours.toFixed(1)} hrs)`);
}

});



/* ---------------- EXPORT PDF ---------------- */

const exportPDF=()=>{

const pdf=new jsPDF();

pdf.setFontSize(18);
pdf.text("Organization Time Tracking Report",20,20);

pdf.setFontSize(12);

let y=40;

logs.forEach(l=>{

pdf.text(
`${l.employeeName} | ${l.projectName} | ${formatTime(l.duration)}`,
20,
y
);

y+=10;

});

pdf.save("time-report.pdf");

};



/* ---------------- LIMIT LISTS ---------------- */

const employeeDisplay =
showAllEmployees ? employeeRanking : employeeRanking.slice(0,5);

const clientDisplay =
showAllClients ? Object.entries(clientTotals) : Object.entries(clientTotals).slice(0,5);



return(

<div className="max-w-7xl mx-auto p-8 space-y-10">

{/* HEADER */}

<div className="flex justify-between items-center">

<h2 className="text-3xl font-bold flex gap-2 items-center">
<BarChart3 size={28}/>
Admin Analytics Dashboard
</h2>

<button
onClick={exportPDF}
className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-lg shadow"
>
Export Report
</button>

</div>



{/* ANALYTICS GRID */}

<div className="grid md:grid-cols-3 gap-6">

{/* EMPLOYEE PRODUCTIVITY */}

<div className="bg-white p-6 rounded-xl shadow">

<h3 className="font-semibold mb-4 flex items-center gap-2">
<Trophy size={18}/>
Employee Productivity
</h3>

{employeeDisplay.map(([name,time],i)=>(

<div key={name} className="flex justify-between py-2 border-b">

<span className="flex gap-2">
<span className="font-bold text-gray-400">#{i+1}</span>
{name}
</span>

<span className="font-bold text-indigo-600">
{formatTime(time)}
</span>

</div>

))}

{employeeRanking.length>5 &&(

<button
onClick={()=>setShowAllEmployees(!showAllEmployees)}
className="flex items-center gap-1 text-indigo-600 mt-3 text-sm"
>

{showAllEmployees ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}

{showAllEmployees ? "Show Less" : "Read More"}

</button>

)}

</div>



{/* CLIENT BREAKDOWN */}

<div className="bg-white p-6 rounded-xl shadow">

<h3 className="font-semibold mb-4">
Client Time Breakdown
</h3>

{clientDisplay.map(([client,time])=>(

<div key={client} className="flex justify-between py-2 border-b">

<span>{client}</span>

<span className="font-bold text-indigo-600">
{formatTime(time)}
</span>

</div>

))}

{Object.entries(clientTotals).length>5 &&(

<button
onClick={()=>setShowAllClients(!showAllClients)}
className="flex items-center gap-1 text-indigo-600 mt-3 text-sm"
>

{showAllClients ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}

{showAllClients ? "Show Less" : "Read More"}

</button>

)}

</div>



{/* PRODUCTIVITY INSIGHTS */}

<div className="bg-white p-6 rounded-xl shadow">

<h3 className="font-semibold mb-4">
Productivity Insights
</h3>

{insights.length===0 &&(
<p className="text-sm text-gray-500">
All employees performing normally
</p>
)}

{insights.map((i,index)=>(

<div
key={index}
className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border mb-2"
>

<span className="text-indigo-600 font-bold">•</span>

<p className="text-sm text-gray-700">
{i}
</p>

</div>

))}

</div>

</div>



{/* SEARCH */}

<div className="relative">

<Search className="absolute left-3 top-3 text-gray-400"/>

<input
type="text"
placeholder="Search employee or project..."
value={search}
onChange={(e)=>setSearch(e.target.value)}
className="border rounded-xl pl-10 pr-4 py-3 w-full focus:ring-2 focus:ring-indigo-500"
/>

</div>



{/* TABLE */}

<div className="bg-white rounded-xl shadow overflow-hidden">

<div className="px-6 py-4 border-b font-semibold text-gray-700">
Time Log History
</div>

<div className="max-h-[400px] overflow-y-auto">

<table className="w-full text-sm">

<thead className="bg-indigo-600 text-white sticky top-0">

<tr>
<th className="p-3 text-left">Employee</th>
<th>Project</th>
<th>Duration</th>
<th>Action</th>
</tr>

</thead>

<tbody>

{filtered.length===0 &&(

<tr>
<td colSpan="4" className="text-center py-10 text-gray-400">
No logs found
</td>
</tr>

)}

{filtered.map(l=>(

<tr key={l.id} className="border-b hover:bg-gray-50 transition">

<td className="p-3 font-medium">{l.employeeName}</td>

<td>{l.projectName}</td>

<td className="font-bold text-indigo-600">
{formatTime(l.duration)}
</td>

<td>

<button
onClick={()=>deleteLog(l.id)}
className="text-red-600 flex items-center gap-1 hover:underline"
>
<Trash2 size={16}/>
Delete
</button>

</td>

</tr>

))}

</tbody>

</table>

</div>

</div>

</div>

);

}

